//--------------------------------------------------------------------//
//******************** trunkInfoTab ************************************//
//--------------------------------------------------------------------//
// Displays and manages editable trunk properties: host, provider,
// username, password, caller ID number (inline edit/save/cancel per
// field, mirroring agent-info.tab.js), register/enabled toggles that save
// immediately on change, and the admins list (other user_ids allowed to
// manage this trunk - see sip-gudhub-server's SipTrunksDatabase.canManage).
import trunkInfoTemplateUrl from './trunk-info.template.html';
import { GUDHUB_SETTINGS } from '../../../../config.js';

angular.module('sipTrunksTableModule')

.directive('trunkInfoTab', ['SipTrunksService', 'GhDialog', function (SipTrunksService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            trunk: '=',
            onUpdate: '&'
        },
        controller: ['$scope', '$element', '$animate', 'localizationService', function ($scope, $element, $animate, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // Same ngAnimate ng-if-swap workaround as agent-info.tab.js /
            // deployment-info.tab.js — this tab has no enter/leave animations
            // of its own, so disable ngAnimate for its whole subtree.
            $animate.enabled(false, $element);

            $scope.showPassword = false;

            // gh-input-list needs a real array to bind gh-model to (a
            // pre-existing trunk created before the admins field existed
            // won't have one).
            $scope.trunk.admins = $scope.trunk.admins || [];

            // Which SMS provider this trunk sends/receives through (twilio
            // or zadarma) - a plain top-level trunk field, defaulted locally
            // for a trunk that predates this feature (provider: null).
            $scope.trunk.provider = $scope.trunk.provider || 'twilio';

            // Defaulted the same way trunk-sms.tab.js does, defensively,
            // since both tabs are instantiated together (see
            // sipTrunksCtrl's openTrunkDetail) and either could render first.
            $scope.trunk.sms = $scope.trunk.sms || { credentials: {}, enabled: false };

            // Read-only reference value — the origination URI a trunk
            // provider (e.g. Twilio) should be configured to send inbound
            // calls to. Not a trunk field, just the app's own SIP domain
            // from config. Port 5080 is the external SIP profile's port
            // (see sip-gudhub-server's external.xml).
            $scope.sipServerUri = 'sip:' + GUDHUB_SETTINGS.sip_domain + ':5080';
            $scope.copiedSipUri = false;

            // Mirrors mcpServers.controller.js's copyEndpointUrl.
            $scope.copySipServerUri = async function (event) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText($scope.sipServerUri);
                    } else {
                        const textarea = document.createElement('textarea');

                        textarea.value = $scope.sipServerUri;
                        textarea.setAttribute('readonly', '');
                        textarea.style.position = 'absolute';
                        textarea.style.left = '-9999px';

                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                    }

                    $scope.copiedSipUri = true;
                    safeApply();

                    setTimeout(function () {
                        $scope.copiedSipUri = false;
                        safeApply();
                    }, 1500);
                } catch (error) {
                    console.log('SIP server URI copy error:', error);
                }
            };

            $scope.editMode = {
                name: false,
                host: false,
                username: false,
                password: false
            };

            $scope.originalValues = {
                name: $scope.trunk.name || '',
                host: $scope.trunk.host || '',
                username: $scope.trunk.username || '',
                password: $scope.trunk.password || ''
            };

            $scope.savingToggle = {
                register: false,
                enabled: false
            };

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // Merges a server response into trunk, except admins: the
            // response's admins is always a fresh array instance (even with
            // identical content), and trunk.admins is watched below to
            // auto-save - merging it back in here would change the watched
            // reference from inside another save's own response handling,
            // spuriously firing a second, unrelated PUT {admins: [...]} for
            // every save on this tab (confirmed live: toggling Enabled fired
            // an extra {"admins":[]} request right after {"enabled":1}).
            function applyTrunkUpdate(updated) {
                const { admins: _ignoredAdmins, ...rest } = updated;
                Object.assign($scope.trunk, rest);
            }

            function showError(message) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_sip_trunk-popup">
                                <div class="dialog-share-group">
                                    <p class="dialog-share-group__title">Error</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: message },
                    controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                        $scope.errorMessage = errorMessage;
                    }]
                });
            }

            // Enters edit mode for a field — stores the original value so
            // Cancel can restore it.
            $scope.enableEdit = function (field) {
                $scope.originalValues[field] = $scope.trunk[field] || '';
                $scope.editMode[field] = true;
            };

            // Exits edit mode and restores the field to its original value.
            $scope.cancelEdit = function (field) {
                $scope.trunk[field] = $scope.originalValues[field];
                $scope.editMode[field] = false;
            };

            // Saves the edited field to the server. Shows an error popup on failure.
            $scope.saveField = async function (field) {
                if ((field === 'host' || field === 'name') && (!$scope.trunk[field] || !$scope.trunk[field].trim().length)) {
                    return;
                }

                try {
                    if (field === 'host' || field === 'name') {
                        $scope.trunk[field] = $scope.trunk[field].trim();
                    }

                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, {
                        [field]: $scope.trunk[field]
                    });

                    applyTrunkUpdate(updated);
                    $scope.originalValues[field] = $scope.trunk[field] || '';
                    $scope.editMode[field] = false;
                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk update error:', error);
                    showError(error.message || 'Failed to update trunk');
                }
            };

            // Saves provider - a plain top-level trunk field, same as
            // host/username, not a dot-path sms.* key: it's shared with
            // voice (see docs/sip-trunks/sms.md).
            $scope.saveProvider = async function () {
                try {
                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, { provider: $scope.trunk.provider });
                    applyTrunkUpdate(updated);
                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk provider update error:', error);
                    showError(error.message || 'Failed to update provider');
                }
            };

            // Register/Enabled are plain checkboxes that save immediately —
            // no edit/save/cancel dance needed for a boolean.
            $scope.saveToggle = async function (field) {
                $scope.savingToggle[field] = true;

                try {
                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, {
                        [field]: $scope.trunk[field]
                    });
                    applyTrunkUpdate(updated);
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk update error:', error);
                    // Revert the checkbox — the server rejected the change.
                    $scope.trunk[field] = !$scope.trunk[field];
                    showError(error.message || 'Failed to update trunk');
                } finally {
                    $scope.savingToggle[field] = false;
                    safeApply();
                }
            };

            // gh-input-list mutates trunk.admins in place by replacing it
            // with a new array on every add/remove (see proxyModel's setter
            // in gh_input_list.js), so a reference watch fires exactly once
            // per user action - no separate save button needed. The first
            // invocation always has newVal === oldVal (same initial
            // reference), which must be skipped or every trunk would issue
            // a spurious update on open. applyTrunkUpdate deliberately never
            // syncs admins back (see its own comment) - without that this
            // watch would re-trigger itself forever, thousands of PUT
            // requests per click (confirmed live).
            $scope.$watch('trunk.admins', async function (newVal, oldVal) {
                if (newVal === oldVal) return;

                try {
                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, { admins: newVal });
                    applyTrunkUpdate(updated);
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk update error:', error);
                    $scope.trunk.admins = oldVal;
                    showError(error.message || 'Failed to update trunk admins');
                } finally {
                    safeApply();
                }
            });
        }],
        templateUrl: trunkInfoTemplateUrl
    };
}]);
